import {
  Box,
  useTheme,
  TextField,
  Typography,
  Button,
  IconButton,
  MenuItem,
  Divider,
  InputAdornment,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import dayjs from "dayjs";
import durationPlugin from "dayjs/plugin/duration";
import { useStateContext } from "contexts/ContextProvider";
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import Grid from "@mui/material/Grid2";
import linguistApi from "services/linguistApi";
import CETimePicker from "components/CETimePicker";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import { OVERLAY_NAMES } from "constants/Overlays";
import { TimeField } from "@mui/x-date-pickers";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import { usePageTitle } from "hooks/usePageTitle";
import { useEditLock } from "hooks/useEditLock";
import EditLockDialog from "components/EditLockDialog";
import Overlay from "components/Overlay/Overlay";
import { useWebSocket } from "contexts/WebSocketContext";
import { usePresence } from "contexts/PresenceContext";
import useAssignmentUpdates from "hooks/useAssignmentUpdates";
import { getBorderColor } from "scenes/services/interpretations/InterpretationNewEdit";
import { useAuth } from "contexts/AuthContext";
import { getMileageRateForDate } from "helpers/mileageRateHelper";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault("America/New_York");

// Helper function for time field button styling
const getTimeFieldButtonSx = (theme) => ({
  padding: "2px",
  bgcolor: theme.palette.background.default,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: "8px",
  "&:hover": {
    bgcolor: theme.palette.primary.main,
    color: theme.palette.background.default,
    border: `1px solid ${theme.palette.primary.main}`,
  },
  "&:disabled": {
    bgcolor: theme.palette.action.disabledBackground,
  },
});

// Helper function to create time field adornments with increment/decrement buttons
const createTimeFieldAdornments = (
  theme,
  disabled,
  onDecrement,
  onIncrement,
) => ({
  startAdornment: (
    <InputAdornment position="start">
      <IconButton
        size="small"
        disabled={disabled}
        onClick={onDecrement}
        sx={getTimeFieldButtonSx(theme)}
      >
        <RemoveRoundedIcon fontSize="small" />
      </IconButton>
    </InputAdornment>
  ),
  endAdornment: (
    <InputAdornment position="end">
      <IconButton
        size="small"
        disabled={disabled}
        onClick={onIncrement}
        sx={getTimeFieldButtonSx(theme)}
      >
        <AddRoundedIcon fontSize="small" />
      </IconButton>
    </InputAdornment>
  ),
});

// Module-level constant for interpretation cost type options.
// Defines all recognized cost types with their metadata and calculation flags.
// includeInLinguistPay: whether the type contributes to TotalLinguistPay calculation.
const INTERPRETATION_BASE_COST_TYPE_OPTIONS = [
  {
    key: "linguistPay",
    label: "Linguist Pay",
    costTypeName: "Linguist Pay",
    sourceLegacyField: "LinguistPay",
    defaultIncludeInTotalCost: true,
    defaultIncludeInAccountCharge: false,
    includeInLinguistPay: false,
  },
  {
    key: "mileageCost",
    label: "Mileage",
    costTypeName: "Mileage",
    sourceLegacyField: "MileageCost",
    defaultIncludeInTotalCost: true,
    defaultIncludeInAccountCharge: true,
    includeInLinguistPay: true,
  },
  {
    key: "parkingFeesTolls",
    label: "Parking Fees",
    costTypeName: "Parking Fees",
    sourceLegacyField: "ParkingFeesTolls",
    defaultIncludeInTotalCost: true,
    defaultIncludeInAccountCharge: true,
    includeInLinguistPay: true,
  },
  {
    key: "additionalCost",
    label: "Additional Cost",
    costTypeName: "Additional Cost",
    sourceLegacyField: "AdditionalCost",
    defaultIncludeInTotalCost: true,
    defaultIncludeInAccountCharge: true,
    includeInLinguistPay: false,
  },
  {
    key: "additionalLinguistCost",
    label: "Additional Linguist Cost",
    costTypeName: "Additional Linguist Cost",
    sourceLegacyField: "AdditionalLinguistCost",
    defaultIncludeInTotalCost: true,
    defaultIncludeInAccountCharge: true,
    includeInLinguistPay: true,
  },
  {
    key: "paymentFees",
    label: "Payment Fees",
    costTypeName: "Payment Fees",
    sourceLegacyField: "PaymentFees",
    defaultIncludeInTotalCost: true,
    defaultIncludeInAccountCharge: false,
    includeInLinguistPay: true,
  },
  {
    key: "equipmentCost",
    label: "Equipment Cost",
    costTypeName: "Equipment",
    sourceLegacyField: "EquipmentCost",
    defaultIncludeInTotalCost: false,
    defaultIncludeInAccountCharge: true,
    includeInLinguistPay: false,
  },
  {
    key: "travelTimeCost",
    label: "Travel Time",
    costTypeName: "Travel Time",
    sourceLegacyField: "TravelTimeCost",
    defaultIncludeInTotalCost: true,
    defaultIncludeInAccountCharge: true,
    includeInLinguistPay: true,
  },
];

const toDayjsTime = (value) => {
  if (!value) return null;
  const parsed = dayjs.isDayjs(value) ? value : dayjs(value, "HH:mm:ss");
  return parsed?.isValid?.() ? parsed : null;
};

const normalizeCostTypeName = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const INTERPRETATION_COST_TYPE_NAME_ALIASES = {
  "parking fees": "parkingFeesTolls",
  "parking fee": "parkingFeesTolls",
  "parking fees and tolls": "parkingFeesTolls",
  "parking fee and tolls": "parkingFeesTolls",
  "parking fees tolls": "parkingFeesTolls",
};

const LinguistAssignmentFinalize = () => {
  
  const { currentUser } = useAuth();
  const { socket } = useWebSocket();
  const { currentEntity, setCurrentEntity } = usePresence();

  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const {
    overlayData,
    setOverlayData,
    setOverlayName,
    triggerPageRefresh,
    showSnackbar,
  } = useStateContext();

  usePageTitle(
    overlayData.Action === OVERLAY_NAMES.INTERPRETATION_ACTION_NEW
      ? "New Interpretation"
      : "Edit Interpretation",
  );

  const [isSavingChanges, setIsSavingChanges] = useState(false);
  const [isEditable, setIsEditable] = useState(true);

  const [, setPendingSave] = useState(false);

  const isNewAssignment =
    overlayData.Action === OVERLAY_NAMES.INTERPRETATION_ACTION_NEW;
  const prefilledData = useMemo(
    () => (isNewAssignment ? overlayData?.PrefilledData || {} : {}),
    [isNewAssignment, overlayData?.PrefilledData],
  );

  // Edit lock for existing services only
  const { isLocked, lockedBy, releaseLock } = useEditLock(
    "service",
    isNewAssignment ? null : overlayData.id,
  );

  // Snapshot the entity the underlying view (list/profile) had before this
  // overlay took over, so closing the overlay can restore it instead of
  // nulling it — nulling would drop the still-mounted list/profile out of its
  // entity room and it would stop receiving live updates.
  const previousEntityRef = useRef(null);
  const hasTakenOverEntityRef = useRef(false);

  // Set entity tracking for presence and updates
  useEffect(() => {
    if (!isNewAssignment && overlayData.id) {
      if (!hasTakenOverEntityRef.current) {
        hasTakenOverEntityRef.current = true;
        // Snapshot only on first takeover: currentEntity is intentionally NOT
        // a dependency, so the closure keeps the underlying view's entity from
        // the render that created this effect.
        previousEntityRef.current = currentEntity;
      }
      setCurrentEntity({
        entityType: "Interpretation",
        entityId: overlayData.id,
      });
    }
    return () => {
      if (hasTakenOverEntityRef.current) {
        // Restore the underlying view's entity (keeps its room alive) instead
        // of clearing it; fall back to clearing when there was nothing to
        // restore.
        setCurrentEntity(
          previousEntityRef.current || { entityType: null, entityId: null },
        );
        hasTakenOverEntityRef.current = false;
        previousEntityRef.current = null;
      }
    };
    // currentEntity intentionally excluded — the effect snapshots the entity
    // from the render that created it (see comment block above).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNewAssignment, overlayData.id, setCurrentEntity]);

  // Listen for assignment updates from other members
  const handleAssignmentRefetch = () => {
    // Reload the overlay data with fresh data from server
    linguistApi
      .get(`/services/${overlayData.id}`)
      .then((response) => {
        setOverlayData({
          ...overlayData,
          ...response.data,
        });
        // Refresh the list behind the overlay, but skip the success snackbar —
        // form fields are local state and don't change on remote updates, so
        // "Assignment data refreshed" would be misleading.
        triggerPageRefresh();
      })
      .catch((err) => console.log("Error refetching assignment:", err));
  };

  useAssignmentUpdates({
    modelType: "Interpretation",
    recordId: isNewAssignment ? null : overlayData.id,
    onUpdate: handleAssignmentRefetch,
  });

  const completing =
    overlayData.Action === OVERLAY_NAMES.INTERPRETATION_ACTION_COMPLETE;

  const [date] = useState(
    isNewAssignment
      ? prefilledData.Date
        ? dayjs.utc(prefilledData.Date)
        : null
      : dayjs.utc(overlayData.Date),
  );

  // Holiday state for the selected date
  const [selectedDateHoliday] = useState(null);

  const [client] = useState(
    isNewAssignment ? prefilledData.Client || "" : overlayData.Client,
  );
  const [requestor] = useState(
    isNewAssignment ? prefilledData.Requestor || "" : overlayData.Requestor,
  );

  const [invoiceTo] = useState(
    isNewAssignment ? prefilledData.Invoicer || "" : overlayData.Invoicer,
  );

  const [language] = useState(
    isNewAssignment
      ? prefilledData.Language || ""
      : overlayData.ServiceTargetLanguages?.[0]?.Language || "",
  );

  const [linguist] = useState(
    isNewAssignment ? prefilledData.Linguist || "" : overlayData.Linguist,
  );

  const [interpretationStatus, setInterpretationStatus] = useState(
    isNewAssignment
      ? prefilledData.InterpretationStatus || "Requested"
      : completing
        ? "Completed"
        : overlayData.InterpretationStatus,
  );

  const [cancellationReason, setCancellationReason] = useState(
    isNewAssignment ? "" : overlayData.CancellationReason,
  );

  const [totalLinguistPayHelperText, setTotalLinguistPayHelperText] =
    useState("");

  // Dynamic map of cost type values - keyed by cost type key (e.g. "mileageCost").
  // New DB cost types are automatically captured without per-type code changes.
  const [costValues, setCostValues] = useState(() => {
    if (isNewAssignment) {
      return Object.fromEntries(
        INTERPRETATION_BASE_COST_TYPE_OPTIONS.map((opt) => [
          opt.key,
          prefilledData[opt.sourceLegacyField] !== undefined &&
          prefilledData[opt.sourceLegacyField] !== null
            ? prefilledData[opt.sourceLegacyField]
            : "",
        ]),
      );
    }
    return Object.fromEntries(
      INTERPRETATION_BASE_COST_TYPE_OPTIONS.map((opt) => [
        opt.key,
        overlayData[opt.sourceLegacyField] !== undefined &&
        overlayData[opt.sourceLegacyField] !== null
          ? overlayData[opt.sourceLegacyField]
          : "",
      ]),
    );
  });
  const [costErrors, setCostErrors] = useState({});

  const [disableAutomaticCalculationOfCharges] = useState(
    isNewAssignment
      ? prefilledData.DisableAutoChargeCalc || false
      : overlayData.DisableAutoChargeCalc || false,
  );

  const [twoHoursMin] = useState(
    isNewAssignment
      ? (linguist && linguist.MinimumHours && linguist.MinimumHours === 2) ||
          false
      : overlayData.ApplyTwoHourMinimum ||
          (linguist && linguist.MinimumHours && linguist.MinimumHours === 2) ||
          false,
  );

  const [allowShortAssignments] = useState(
    isNewAssignment
      ? prefilledData.IsShortAssignment || false
      : overlayData.IsShortAssignment || false,
  );
  // Alias const derivations for backward compatibility with save payloads, auto-calc, etc.
  const linguistPay = costValues.linguistPay ?? "";
  const additionalCost = costValues.additionalCost ?? 0;
  const additionalLinguistCost = costValues.additionalLinguistCost ?? "";
  const equipmentCost = costValues.equipmentCost ?? "";
  const mileageCost = costValues.mileageCost ?? 0;
  const parkingFeesTolls = costValues.parkingFeesTolls ?? 0;
  const paymentFees = costValues.paymentFees ?? 0;
  const travelTimeCost = costValues.travelTimeCost ?? 0;

  // Alias setter functions for backward compatibility
  const setLinguistPay = (v) =>
    setCostValues((prev) => ({ ...prev, linguistPay: v }));
  const setAdditionalLinguistCost = (v) =>
    setCostValues((prev) => ({ ...prev, additionalLinguistCost: v }));
  const setMileageCost = (v) =>
    setCostValues((prev) => ({ ...prev, mileageCost: v }));
  const setParkingFeesTolls = (v) =>
    setCostValues((prev) => ({ ...prev, parkingFeesTolls: v }));
  const setTravelTimeCost = (v) =>
    setCostValues((prev) => ({ ...prev, travelTimeCost: v }));

  const [mode] = useState(
    overlayData.Mode === null ||
      overlayData.Mode === "" ||
      overlayData.Mode === undefined
      ? "Consecutive"
      : overlayData.Mode,
  );

  const getDurationValue = useCallback((timeStart, timeEnd) => {
    if (timeStart && timeEnd && timeStart.isValid() && timeEnd.isValid()) {
      const diffMs = timeEnd.diff(timeStart);

      // Convert to minutes and round up to nearest 15-minute increment
      const minutes = diffMs / 1000 / 60;
      const roundedMinutes = Math.ceil(minutes / 15) * 15;
      const roundedMs = roundedMinutes * 60 * 1000;

      return dayjs.utc(roundedMs);
    }
    return null;
  }, []);

  const [rate] = useState(
    isNewAssignment ? Number(prefilledData.Rate || 0) : overlayData.Rate,
  );

  // 2026-10-06 (a) model ("calculate by default, your entry wins") — manual
  // mode (DisableAutoChargeCalc): a stored non-null, non-zero Total Linguist
  // Pay is a USER OVERRIDE and wins; null/0 means "calculated" (the live
  // computation fills the field; Save sends null so it never freezes).
  const storedTotalLinguistPayAtInit = isNewAssignment
    ? prefilledData.TotalLinguistPay
    : overlayData.TotalLinguistPay;
  const hasTotalLinguistPayOverrideAtInit =
    storedTotalLinguistPayAtInit != null &&
    storedTotalLinguistPayAtInit !== "" &&
    Number(storedTotalLinguistPayAtInit) !== 0;
  const totalLinguistPayOverriddenRef = useRef(
    hasTotalLinguistPayOverrideAtInit,
  );

  const [totalLinguistPay, setTotalLinguistPay] = useState(
    disableAutomaticCalculationOfCharges && !hasTotalLinguistPayOverrideAtInit
      ? ""
      : isNewAssignment
        ? prefilledData.TotalLinguistPay || ""
        : overlayData.TotalLinguistPay,
  );

  const [timeStart] = useState(
    isNewAssignment
      ? prefilledData.TimeStart
        ? toDayjsTime(prefilledData.TimeStart)
        : ""
      : dayjs(overlayData.TimeStart, "HH:mm:ss"),
  );

  const [timeEnd] = useState(
    isNewAssignment
      ? prefilledData.TimeEnd
        ? toDayjsTime(prefilledData.TimeEnd)
        : ""
      : dayjs(overlayData.TimeEnd, "HH:mm:ss"),
  );

  const [duration] = useState(
    isNewAssignment
      ? prefilledData.TimeStart && prefilledData.TimeEnd
        ? getDurationValue(
            toDayjsTime(prefilledData.TimeStart),
            toDayjsTime(prefilledData.TimeEnd),
          )
        : null
      : dayjs(overlayData.Duration, "HH:mm:ss"),
  );

  // Initialize actual times as null
  const [actualStart, setActualStart] = useState(
    isNewAssignment
      ? prefilledData.ActualStart
        ? toDayjsTime(prefilledData.ActualStart)
        : ""
      : overlayData.ActualStart === "00:00:00"
        ? completing
          ? dayjs(overlayData.TimeStart, "HH:mm:ss")
          : null
        : dayjs(overlayData.ActualStart, "HH:mm:ss"),
  );
  const [actualEnd, setActualEnd] = useState(
    isNewAssignment
      ? prefilledData.ActualEnd
        ? toDayjsTime(prefilledData.ActualEnd)
        : ""
      : overlayData.ActualEnd === "00:00:00"
        ? completing
          ? dayjs(overlayData.TimeEnd, "HH:mm:ss")
          : null
        : dayjs(overlayData.ActualEnd, "HH:mm:ss"),
  );
  const [actualDuration, setActualDuration] = useState(
    isNewAssignment
      ? prefilledData.ActualDuration
        ? toDayjsTime(prefilledData.ActualDuration)
        : prefilledData.ActualStart && prefilledData.ActualEnd
          ? getDurationValue(
              toDayjsTime(prefilledData.ActualStart),
              toDayjsTime(prefilledData.ActualEnd),
            )
          : null
      : overlayData.ActualDuration === "00:00:00"
        ? completing
          ? dayjs(overlayData.Duration, "HH:mm:ss")
          : null
        : dayjs(overlayData.ActualDuration, "HH:mm:ss"),
  );

  // Also initialize scheduling and preparation times as null
  const [schedulingTime, setSchedulingTime] = useState(
    isNewAssignment
      ? prefilledData.SchedulingTime
        ? toDayjsTime(prefilledData.SchedulingTime)
        : null
      : overlayData.SchedulingTime === "00:00:00"
        ? null
        : dayjs(overlayData.SchedulingTime, "HH:mm:ss"),
  );
  const [preparationTime, setPreparationTime] = useState(
    isNewAssignment
      ? prefilledData.PreparationTime
        ? toDayjsTime(prefilledData.PreparationTime)
        : null
      : overlayData.PreparationTime === "00:00:00"
        ? null
        : dayjs(overlayData.PreparationTime, "HH:mm:ss"),
  );
  const [travelTimeDuration, setTravelTimeDuration] = useState(
    isNewAssignment
      ? prefilledData.TravelTimeDuration
        ? toDayjsTime(prefilledData.TravelTimeDuration)
        : null
      : overlayData.TravelTimeDuration === "00:00:00" ||
          !overlayData.TravelTimeDuration
        ? null
        : dayjs(overlayData.TravelTimeDuration, "HH:mm:ss"),
  );
  const [totalDuration, setTotalDuration] = useState(
    isNewAssignment
      ? prefilledData.TimeStart && prefilledData.TimeEnd
        ? getDurationValue(
            toDayjsTime(prefilledData.TimeStart),
            toDayjsTime(prefilledData.TimeEnd),
          )
        : null
      : overlayData.TotalDuration === "00:00:00"
        ? completing
          ? dayjs(overlayData.Duration, "HH:mm:ss")
          : null
        : dayjs(overlayData.TotalDuration, "HH:mm:ss"),
  );

  const [mileageRoundtrip, setMileageRoundtrip] = useState(
    isNewAssignment
      ? Number(prefilledData.MileageRoundtrip || 0)
      : overlayData.MileageRoundtrip,
  );

  const [shouldAutoCalculateMileageCost, setShouldAutoCalculateMileageCost] =
    useState(() => {
      if (isNewAssignment) return true;
      return !(
        overlayData?.MileageCost === null ||
        overlayData?.MileageCost === undefined ||
        overlayData?.MileageCost === ""
      );
    });

  const [mileageRates, setMileageRates] = useState([]);

  const getMileageRateForYear = useCallback(
    (dateValue) => {
      if (!dateValue) return 0.7;
      return getMileageRateForDate(mileageRates, dateValue, 0.7);
    },
    [mileageRates],
  );

  const [interpretationStatusError, setInterpretationStatusError] =
    useState(false);

  const linguistPayError = !!costErrors.linguistPay;
  const setLinguistPayError = (v) =>
    setCostErrors((prev) => ({ ...prev, linguistPay: v }));

  const setAdditionalLinguistCostError = (v) =>
    setCostErrors((prev) => ({ ...prev, additionalLinguistCost: v }));

  const [timeStartError, setTimeStartError] = useState(false);
  const [timeEndError, setTimeEndError] = useState(false);
  useState(false);
  const [actualStartError, setActualStartError] = useState(false);
  const [actualEndError, setActualEndError] = useState(false);
  const [schedulingTimeError, setSchedulingTimeError] = useState(false);
  const [mileageRoundtripError, setMileageRoundtripError] = useState(false);
  const setParkingFeesTollsError = (v) =>
    setCostErrors((prev) => ({ ...prev, parkingFeesTolls: v }));
  const setMileageCostError = (v) =>
    setCostErrors((prev) => ({ ...prev, mileageCost: v }));

  const [shouldValidate, setShouldValidate] = useState(false);
  const [costRows, setCostRows] = useState([]);
  const [nextCostRowId, setNextCostRowId] = useState(1);
  const [originalCostValuesByType, setOriginalCostValuesByType] = useState({});

  // costTypeRows: raw API response — ALL types for this category, not just known ones
  const [costTypeRows, setCostTypeRows] = useState([]);

  useEffect(() => {
    let isMounted = true;

    const loadCostTypeRules = async () => {
      try {
        const response = await linguistApi.get(
          "/serviceCosts/types/by-category/Interpretation",
        );

        const rows = Array.isArray(response.data) ? response.data : [];

        if (!isMounted) return;

        setCostTypeRows(rows);
      } catch (error) {
        console.error("Error loading interpretation cost type rules:", error);
      }
    };

    loadCostTypeRules();

    return () => {
      isMounted = false;
    };
  }, []);

  // costTypeOptions: built from all API rows so new DB types appear automatically.
  // Falls back to INTERPRETATION_BASE_COST_TYPE_OPTIONS before first API load.
  const costTypeOptions = useMemo(() => {
    const toKey = (str) =>
      str ? str.charAt(0).toLowerCase() + str.slice(1) : str;
    if (costTypeRows.length === 0) {
      return INTERPRETATION_BASE_COST_TYPE_OPTIONS.map((opt) => ({
        ...opt,
        isEnabled: true,
        effectiveIncludeInTotalCost: opt.defaultIncludeInTotalCost,
        effectiveIncludeInAccountCharge: opt.defaultIncludeInAccountCharge,
      }));
    }
    const rowsMapped = costTypeRows.map((row) => {
      const normalizedRowName = normalizeCostTypeName(row.Name);
      const aliasBaseKey =
        INTERPRETATION_COST_TYPE_NAME_ALIASES[normalizedRowName];
      const base = INTERPRETATION_BASE_COST_TYPE_OPTIONS.find(
        (b) =>
          b.sourceLegacyField === row.SourceLegacyField ||
          normalizeCostTypeName(b.costTypeName) === normalizedRowName ||
          normalizeCostTypeName(b.label) === normalizedRowName ||
          b.key === aliasBaseKey,
      );
      const key = base?.key || toKey(row.SourceLegacyField || row.Name);
      return {
        key,
        label: base?.label || row.Name,
        costTypeName: row.Name,
        sourceLegacyField: row.SourceLegacyField || null,
        includeInLinguistPay: base?.includeInLinguistPay ?? false,
        defaultIncludeInTotalCost:
          base?.defaultIncludeInTotalCost ?? row.EffectiveIncludeInTotalCost,
        defaultIncludeInAccountCharge:
          base?.defaultIncludeInAccountCharge ??
          row.EffectiveIncludeInAccountCharge,
        isEnabled: row.IsEnabledForCategory,
        effectiveIncludeInTotalCost: row.EffectiveIncludeInTotalCost,
        effectiveIncludeInAccountCharge: row.EffectiveIncludeInAccountCharge,
        visibleToLinguists: row.VisibleToLinguists,
      };
    });

    // Server has already filtered to only VisibleToLinguists=true in the API response.
    // Just return the mapped rows directly without additional client-side filtering.
    return rowsMapped;
  }, [costTypeRows]);

  // Dynamic lookup — handles known types and any new DB types
  const resolveCostTypeKeyFromLine = useCallback(
    (line) => {
      const sourceLegacyField =
        line?.SourceLegacyField || line?.CostType?.SourceLegacyField || "";
      const costTypeName =
        line?.CostTypeName || line?.CostType?.Name || line?.CustomLabel || "";
      if (sourceLegacyField) {
        const opt = costTypeOptions.find(
          (o) =>
            String(o.sourceLegacyField || "").toLowerCase() ===
            sourceLegacyField.toLowerCase(),
        );
        if (opt) return opt.key;
        const base = INTERPRETATION_BASE_COST_TYPE_OPTIONS.find(
          (o) =>
            String(o.sourceLegacyField || "").toLowerCase() ===
            sourceLegacyField.toLowerCase(),
        );
        if (base) return base.key;
      }
      if (costTypeName) {
        const normalizedName = normalizeCostTypeName(costTypeName);
        const aliasKey = INTERPRETATION_COST_TYPE_NAME_ALIASES[normalizedName];
        if (aliasKey) return aliasKey;

        const opt = costTypeOptions.find(
          (o) =>
            normalizeCostTypeName(o.costTypeName) === normalizedName ||
            normalizeCostTypeName(o.label) === normalizedName,
        );
        if (opt) return opt.key;

        const base = INTERPRETATION_BASE_COST_TYPE_OPTIONS.find(
          (o) =>
            normalizeCostTypeName(o.costTypeName) === normalizedName ||
            normalizeCostTypeName(o.label) === normalizedName,
        );
        if (base) return base.key;
      }
      return null;
    },
    [costTypeOptions],
  );

  const getInitialCostTypeKeys = useCallback(
    (data) => {
      const preferredOrder = [
        "linguistPay",
        ...costTypeOptions
          .filter((o) => o.key !== "linguistPay")
          .map((o) => o.key),
      ];

      const nonZeroFromCostLines = new Set(
        (Array.isArray(data?.CostLines) ? data.CostLines : [])
          .filter((line) => Number(line?.Amount || 0) !== 0)
          .map((line) => resolveCostTypeKeyFromLine(line))
          .filter(Boolean),
      );

      const hasCostLinesData = nonZeroFromCostLines.size > 0;

      const nonZeroFromLegacy = new Set(
        INTERPRETATION_BASE_COST_TYPE_OPTIONS.filter(
          (opt) => Number(data?.[opt.sourceLegacyField] || 0) !== 0,
        ).map((opt) => opt.key),
      );

      const activeSet = hasCostLinesData
        ? nonZeroFromCostLines
        : nonZeroFromLegacy;

      return preferredOrder.filter(
        (key) => key === "linguistPay" || activeSet.has(key),
      );
    },
    [resolveCostTypeKeyFromLine, costTypeOptions],
  );

  const getNonZeroLegacyTypeKeys = useCallback((data) => {
    return INTERPRETATION_BASE_COST_TYPE_OPTIONS.filter((opt) => {
      const value = Number(data?.[opt.sourceLegacyField] || 0);
      return Number.isFinite(value) && value !== 0;
    }).map((opt) => opt.key);
  }, []);

  const getCostLineAmountsByType = useCallback(
    (data) => {
      const amounts = {};
      const lines = Array.isArray(data?.CostLines) ? data.CostLines : [];

      lines.forEach((line) => {
        const key = resolveCostTypeKeyFromLine(line);
        const amount = Number(line?.Amount || 0);
        if (!key || Number.isNaN(amount)) return;
        amounts[key] = amount;
      });

      return amounts;
    },
    [resolveCostTypeKeyFromLine],
  );

  const getCostValueByType = useCallback(
    (typeKey) => {
      switch (typeKey) {
        case "mileageCost":
          return mileageCost;
        case "parkingFeesTolls":
          return parkingFeesTolls;
        case "linguistPay":
          return linguistPay;
        case "additionalLinguistCost":
          return additionalLinguistCost;
        case "additionalCost":
          return additionalCost;
        case "paymentFees":
          return paymentFees;
        case "equipmentCost":
          return equipmentCost;
        case "travelTimeCost":
          return travelTimeCost;
        default:
          return "";
      }
    },
    [
      mileageCost,
      parkingFeesTolls,
      linguistPay,
      additionalLinguistCost,
      additionalCost,
      paymentFees,
      equipmentCost,
      travelTimeCost,
    ],
  );

  const getOriginalCostValueByType = (typeKey) => {
    if (
      Object.prototype.hasOwnProperty.call(originalCostValuesByType, typeKey)
    ) {
      return originalCostValuesByType[typeKey];
    }

    switch (typeKey) {
      case "mileageCost":
        return overlayData.MileageCost;
      case "parkingFeesTolls":
        return overlayData.ParkingFeesTolls;
      case "linguistPay":
        return overlayData.LinguistPay;
      case "additionalLinguistCost":
        return overlayData.AdditionalLinguistCost;
      case "additionalCost":
        return overlayData.AdditionalCost;
      case "paymentFees":
        return overlayData.PaymentFees;
      case "equipmentCost":
        return overlayData.EquipmentCost;
      case "travelTimeCost":
        return overlayData.TravelTimeCost;
      default:
        return "";
    }
  };

  const setCostValueByType = useCallback((typeKey, value) => {
    switch (typeKey) {
      case "mileageCost":
        setMileageCost(value);
        setMileageCostError(value === null);
        break;
      case "parkingFeesTolls":
        setParkingFeesTolls(value);
        setParkingFeesTollsError(value === null);
        break;
      case "linguistPay":
        setLinguistPay(value);
        setLinguistPayError(value === null);
        break;
      case "additionalLinguistCost":
        setAdditionalLinguistCost(value);
        setAdditionalLinguistCostError(value === null);
        break;
      case "travelTimeCost":
        setTravelTimeCost(value);
        break;
      default:
        break;
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    const initializeCostRows = async () => {
      if (isNewAssignment) {
        const prefilledCostData = {
          ...prefilledData,
          CostLines:
            Array.isArray(prefilledData?.CostLines) &&
            prefilledData.CostLines.length > 0
              ? prefilledData.CostLines
              : Array.isArray(overlayData?.CostLines)
                ? overlayData.CostLines
                : [],
        };

        const initialTypeKeys = getInitialCostTypeKeys(prefilledCostData);
        const amountsFromCostLines =
          getCostLineAmountsByType(prefilledCostData);
        const nonZeroLegacyTypeKeys =
          getNonZeroLegacyTypeKeys(prefilledCostData);

        if (!isMounted) return;

        setOriginalCostValuesByType(amountsFromCostLines);

        Object.entries(amountsFromCostLines).forEach(([typeKey, amount]) => {
          if (Number(amount || 0) !== 0) {
            setCostValueByType(typeKey, String(amount));
          }
        });

        const ensuredTypeKeys = Array.from(
          new Set([
            "linguistPay",
            ...initialTypeKeys,
            ...nonZeroLegacyTypeKeys,
          ]),
        );

        const rows = ensuredTypeKeys.map((typeKey, index) => ({
          id: index + 1,
          typeKey,
        }));

        setCostRows(rows);
        setNextCostRowId(rows.length + 1);
        return;
      }

      let fetchedCostLines = [];
      
      if (overlayData?.id) {
        try {
          const response = await linguistApi.get(
            `/service/${overlayData.id}/costLines`,
          );
          fetchedCostLines = Array.isArray(response.data?.lines)
            ? response.data.lines
            : [];

        } catch (error) {
          console.error("Error loading service cost lines:", error);
        }
      }

      const overlayDataWithCostLines = {
        ...overlayData,
        CostLines:
          fetchedCostLines.length > 0
            ? fetchedCostLines
            : Array.isArray(overlayData?.CostLines)
              ? overlayData.CostLines
              : [],
      };

      const initialTypeKeys = getInitialCostTypeKeys(overlayDataWithCostLines);
      const amountsFromCostLines = getCostLineAmountsByType(
        overlayDataWithCostLines,
      );
      const nonZeroLegacyTypeKeys = getNonZeroLegacyTypeKeys(
        overlayDataWithCostLines,
      );

      if (!isMounted) return;

      setOriginalCostValuesByType(amountsFromCostLines);

      Object.entries(amountsFromCostLines).forEach(([typeKey, amount]) => {
        if (Number(amount || 0) !== 0) {
          setCostValueByType(typeKey, String(amount));
        }
      });

      const ensuredTypeKeys = Array.from(
        new Set([...initialTypeKeys, ...nonZeroLegacyTypeKeys]),
      );

      const rows = ensuredTypeKeys.map((typeKey, index) => ({
        id: index + 1,
        typeKey,
      }));

      setCostRows(rows);
      setNextCostRowId(rows.length + 1);
    };

    initializeCostRows();

    return () => {
      isMounted = false;
    };
  }, [
    isNewAssignment,
    prefilledData,
    overlayData,
    overlayData?.id,
    getCostLineAmountsByType,
    getInitialCostTypeKeys,
    getNonZeroLegacyTypeKeys,
    setCostValueByType,
  ]);

  // Keep cost rows in sync with non-zero values so any cost with value is always visible.
  useEffect(() => {
    const visibleTypeKeys = new Set(costRows.map((row) => row.typeKey));

    const missingKeys = costTypeOptions
      .filter((opt) => opt.key !== "linguistPay")
      .map((opt) => opt.key)
      .filter((key) => {
        const value = Number(getCostValueByType(key) || 0);
        return (
          Number.isFinite(value) && value !== 0 && !visibleTypeKeys.has(key)
        );
      });

    if (missingKeys.length === 0) return;

    setCostRows((prev) => {
      const alreadyUsed = new Set(prev.map((row) => row.typeKey));
      const rowsToAdd = missingKeys
        .filter((key) => !alreadyUsed.has(key))
        .map((key, index) => ({
          id: nextCostRowId + index,
          typeKey: key,
        }));

      return rowsToAdd.length ? [...prev, ...rowsToAdd] : prev;
    });

    setNextCostRowId((prev) => prev + missingKeys.length);
  }, [
    costRows,
    costTypeOptions,
    getCostValueByType,
    nextCostRowId,
    mileageCost,
    parkingFeesTolls,
    additionalCost,
    additionalLinguistCost,
    paymentFees,
    equipmentCost,
    travelTimeCost,
  ]);

  const addCostRow = () => {
    const usedKeys = new Set(costRows.map((row) => row.typeKey));
    const nextOption = costTypeOptions.find(
      (option) =>
        option.key !== "linguistPay" &&
        option.isEnabled &&
        !usedKeys.has(option.key),
    );
    if (!nextOption) return;

    setCostRows((prev) => [
      ...prev,
      { id: nextCostRowId, typeKey: nextOption.key },
    ]);
    setNextCostRowId((prev) => prev + 1);
  };

  const removeCostRow = (rowId) => {
    const row = costRows.find((item) => item.id === rowId);
    if (row?.typeKey === "linguistPay") {
      return;
    }
    if (row?.typeKey) {
      setCostValueByType(row.typeKey, "");
    }
    setCostRows((prev) => prev.filter((item) => item.id !== rowId));
  };

  const updateCostRowType = (rowId, nextTypeKey) => {
    const row = costRows.find((item) => item.id === rowId);
    if (row?.typeKey === "linguistPay" && nextTypeKey !== "linguistPay") {
      return;
    }
    if (row?.typeKey && row.typeKey !== nextTypeKey) {
      setCostValueByType(row.typeKey, "");
    }

    if (nextTypeKey === "mileageCost") {
      setShouldAutoCalculateMileageCost(true);
    }

    setCostRows((prev) =>
      prev.map((item) =>
        item.id === rowId
          ? {
              ...item,
              typeKey: nextTypeKey,
            }
          : item,
      ),
    );
  };

  const [, setShakingFields] = useState(new Set());

  const [, setHasChanges] = useState(false);

  const initialDataRef = useRef(null);
  const updateCurrentProgressRef = useRef(() => {});
  const updateInterpretationRef = useRef(() => {});
  const saveInterpretationRef = useRef(() => {});
  const attemptSaveRef = useRef(() => {});
  const backButtonLongPressTriggeredRef = useRef(false);

  const handleBackButtonClick = useCallback(() => {
    if (backButtonLongPressTriggeredRef.current) {
      backButtonLongPressTriggeredRef.current = false;
      return;
    }

    releaseLock();
    setOverlayName("");
  }, [releaseLock, setOverlayName]);

  const getBorderColorTimes = (newData, oldData, newData2) => {
    if (!isNewAssignment && newData !== oldData) {
      return theme.palette.info[500];
    } else {
      return newData === null || newData === ""
        ? newData2 === null && newData2 === ""
          ? theme.palette.primary.main
          : theme.palette.error.main
        : theme.palette.primary.main;
    }
  };

  /**
   * VALIDATION CONFIGURATION
   * Easily manage required fields and their validation behavior
   *
   * Structure: { fieldId, value, message, isRequired }
   * - isRequired: true = must be filled before save, false = shows in progress message
   */
  const getValidationFields = () => [
    // REQUIRED FIELDS (Must be filled)

    {
      fieldId: "StartTime",
      value: timeStart,
      message: "Please add a Start Time",
      isRequired: false,
    },
    {
      fieldId: "EndTime",
      value: timeEnd,
      message: "Please add an End Time",
      isRequired: false,
    },
    {
      fieldId: "ActualStart",
      value: actualStart,
      message: "Please add a Start Time",
      isRequired: true,
    },
    {
      fieldId: "ActualEnd",
      value: actualEnd,
      message: "Please add the Actual End Time",
      isRequired: true,
    },
    {
      fieldId: "InterpretationStatus",
      value: interpretationStatus,
      message: "Please add an Interpretation Status",
      isRequired: true,
    },
  ];

  /**
   * Trigger shake animation on missing required fields
   */
  const triggerMissingFieldsShake = () => {
    const validationFields = getValidationFields();
    const missingRequiredFields = validationFields.filter(
      (field) =>
        field.isRequired && (field.value === null || field.value === ""),
    );

    if (missingRequiredFields.length > 0) {
      const newShakingFields = new Set(
        missingRequiredFields.map((field) => field.fieldId),
      );
      setShakingFields(newShakingFields);

      // Remove shake effect after animation completes (600ms)
      setTimeout(() => {
        setShakingFields(new Set());
      }, 600);

      return false; // Validation failed
    }

    return true; // Validation passed
  };

  dayjs.extend(durationPlugin);

  const sumDurationValues = (durations) => {
    const totalMinutes = durations.reduce((acc, d) => {
      if (!d) return acc;

      // Handle dayjs objects
      if (d.format) {
        const timeString = d.format("HH:mm");
        const [hours, minutes] = timeString.split(":").map(Number);
        return acc + hours * 60 + minutes;
      }

      // Handle numeric values (already in minutes)
      return acc + d;
    }, 0);

    return dayjs().startOf("day").add(totalMinutes, "minute");
  };

  const validateFields = () => {
    const errors = {
      interpretationStatus: interpretationStatus === null,
      timeStart: timeStart === null,
      timeEnd: timeEnd === null,
      actualStart: timeStart === null,
      actualEnd: timeEnd === null,
      linguistPay: linguistPay === null,
    };

    setInterpretationStatusError(errors.interpretationStatus);
    setTimeStartError(errors.timeStart);
    setTimeEndError(errors.timeEnd);
    setActualStartError(errors.actualStart);
    setActualEndError(errors.actualEnd);
    setLinguistPayError(errors.linguistPay);

    return errors;
  };

  const buildDynamicCostLines = () => {
    const toAmount = (value) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? Number(parsed.toFixed(2)) : 0;
    };

    const lineConfigByType = costTypeOptions.reduce((acc, option) => {
      acc[option.key] = {
        CostTypeName: option.costTypeName,
        SourceLegacyField: option.sourceLegacyField,
        IncludeInTotalCost: option.effectiveIncludeInTotalCost,
        IncludeInAccountCharge: option.effectiveIncludeInAccountCharge,
      };
      return acc;
    }, {});

    // Build lines from costRows (user-added rows)
    const costRowLines = costRows
      .map((row, index) => {
        const config = lineConfigByType[row.typeKey];
        if (!config) return null;

        return {
          ...config,
          Amount: toAmount(getCostValueByType(row.typeKey)),
          DisplayOrder: (index + 1) * 10,
        };
      })
      .filter(Boolean)
      .filter((line) => line.Amount > 0);

    // Also include base cost types (linguistPay, paymentFees, etc) that have non-zero values
    // even if they're not explicitly in costRows (handles auto-calculated values)
    const baseTypeKeys = new Set(["linguistPay", "paymentFees"]);
    const costRowTypeKeys = new Set(costRows.map((row) => row.typeKey));

    const baseLines = Array.from(baseTypeKeys)
      .filter(
        (typeKey) => !costRowTypeKeys.has(typeKey) && lineConfigByType[typeKey],
      )
      .map((typeKey, index) => {
        const config = lineConfigByType[typeKey];
        const amount = toAmount(getCostValueByType(typeKey));
        if (amount === 0) return null;

        return {
          ...config,
          Amount: amount,
          DisplayOrder: (costRows.length + index + 1) * 10,
        };
      })
      .filter(Boolean);

    return [...costRowLines, ...baseLines];
  };

  const getMileagePayloadValues = useCallback(() => {
    return {
      mileageRoundtripValue: mileageRoundtrip === "" ? null : mileageRoundtrip,
      mileageCostValue: mileageCost === "" ? null : mileageCost,
    };
  }, [mileageRoundtrip, mileageCost]);

  function updateInterpretation() {
    setIsSavingChanges(true);
    setIsEditable(false);

    try {
      const { mileageRoundtripValue } = getMileagePayloadValues();
      const dynamicCostLines = buildDynamicCostLines();

      const dataOld = {
        Assignment: {
          id: overlayData.id,
          TotalDuration: overlayData.TotalDuration,
          ActualDuration: overlayData.ActualDuration,
          InterpretationStatus:
            overlayData.InterpretationStatus === ""
              ? null
              : overlayData.InterpretationStatus,
          MileageRoundtrip:
            overlayData.MileageRoundtrip === "" ||
            overlayData.MileageRoundtrip === null
              ? "0.00"
              : overlayData.MileageRoundtrip,
          TravelTimeDuration: overlayData.TravelTimeDuration || null,
          PreparationTime: overlayData.PreparationTime,
          SchedulingTime: overlayData.SchedulingTime,
          ActualStart: overlayData.ActualStart,
          ActualEnd: overlayData.ActualEnd,
          TotalLinguistPay:
            overlayData.TotalLinguistPay === ""
              ? null
              : overlayData.TotalLinguistPay,
          AdditionalLinguistCost:
            overlayData.AdditionalLinguistCost === ""
              ? null
              : overlayData.AdditionalLinguistCost,
          DisableAutoChargeCalc: overlayData.DisableAutoChargeCalc || false,
          ApplyTwoHourMinimum: overlayData.ApplyTwoHourMinimum || false,
          IsShortAssignment: overlayData.IsShortAssignment || false,
          Cancelled: overlayData.Cancelled,
          CancellationReason: overlayData.CancellationReason,
        },
        Language: overlayData.ServiceTargetLanguages?.[0]?.Language || null,
        Client: overlayData.Client,
        Requestor: overlayData.Requestor,
        Linguist: overlayData.Linguist,
        Invoicer: overlayData.Invoicer,
      };

      const dataNew = {
        Assignment: {
          id: overlayData.id,
          TimeStart: (timeStart && timeStart.format("HH:mm:ss")) || "00:00:00",
          TimeEnd: (timeEnd && timeEnd.format("HH:mm:ss")) || "00:00:00",
          TotalDuration:
            (totalDuration && totalDuration.format("HH:mm:ss")) || "00:00:00",
          Duration: (duration && duration.format("HH:mm:ss")) || "00:00:00",
          ActualDuration:
            (actualDuration && actualDuration.format("HH:mm:ss")) || "00:00:00",
          InterpretationStatus:
            interpretationStatus === "" ? null : interpretationStatus,
          MileageRoundtrip: mileageRoundtripValue,
          ParkingFeesTolls: parkingFeesTolls === "" ? null : parkingFeesTolls,
          TravelTimeDuration:
            (travelTimeDuration && travelTimeDuration.format("HH:mm:ss")) ||
            null,
          PreparationTime:
            (preparationTime && preparationTime.format("HH:mm:ss")) ||
            "00:00:00",
          SchedulingTime:
            (schedulingTime && schedulingTime.format("HH:mm:ss")) || "00:00:00",
          ActualStart:
            (actualStart && actualStart.format("HH:mm:ss")) || "00:00:00",
          ActualEnd: (actualEnd && actualEnd.format("HH:mm:ss")) || "00:00:00",
          // 2026-10-06 (a) model: manual mode — send null for a computed
          // (non-overridden) Total Linguist Pay so it never freezes as an
          // override on reopen.
          TotalLinguistPay:
            disableAutomaticCalculationOfCharges &&
            !totalLinguistPayOverriddenRef.current
              ? null
              : totalLinguistPay === ""
                ? null
                : Number(totalLinguistPay).toFixed(2),
          AdditionalLinguistCost:
            additionalLinguistCost === ""
              ? null
              : Number(additionalLinguistCost).toFixed(2),
          DisableAutoChargeCalc: disableAutomaticCalculationOfCharges,
          ApplyTwoHourMinimum: twoHoursMin,
          IsShortAssignment: allowShortAssignments,
          Cancelled:
            interpretationStatus !== null
              ? interpretationStatus.startsWith("Cancel")
              : false,
          CancellationReason:
            cancellationReason === "" ? null : cancellationReason,
        },
        Language: language,
        Client: client,
        Requestor: requestor,
        Linguist: linguist,
        Invoicer: invoiceTo,
        CostLines: dynamicCostLines,
      };

      const allData = {
        Old: dataOld,
        New: dataNew,
      };

      linguistApi
        .post("/services/interpretations/updateInterpretation", {
          data: allData,
        })
        .then((response) => {
          
          setIsSavingChanges(false);
          triggerPageRefresh(
            "Interpretation Assignment Updated Sucessfully",
            theme,
          );

          // Emit assignment saved event for other viewers
          if (socket) {
            socket.emit("assignment:saved", {
              modelType: "Interpretation",
              recordId: overlayData.id,
              user: {
                id: currentUser.id,
                name: currentUser.Name,
                imageThumbnail: currentUser.ImageThumbnail,
              },
              projectNumber: overlayData.ProjectNumber,
            });

            // Also notify the interpretations list page to refetch
            socket.emit("assignment:saved", {
              modelType: "InterpretationsList",
              recordId: "all",
              user: {
                id: currentUser.id,
                name: currentUser.Name,
                imageThumbnail: currentUser.ImageThumbnail,
              },
              projectNumber: overlayData.ProjectNumber,
            });
          }

          releaseLock();
          setOverlayName("");
        })
        .catch((err) => {
          console.error("Failed to update interpretation:", err);
          setIsSavingChanges(false);
          setIsEditable(true);
          triggerPageRefresh(
            "Failed to update interpretation assignment",
            theme,
          );
          showSnackbar(
            "Failed to update interpretation assignment",
            theme,
            "error",
          );
        });
    } catch (err) {
      console.error("Failed to build interpretation update payload:", err);
      setIsSavingChanges(false);
      setIsEditable(true);
      triggerPageRefresh("Failed to prepare interpretation update", theme);
      showSnackbar("Failed to prepare interpretation update", theme, "error");
    }
  }

  

  // Fetch mileage rates on component mount
  useEffect(() => {
    const fetchMileageRates = async () => {
      try {
        const response = await linguistApi.get("/mileageRates");
        setMileageRates(response.data || []);
      } catch (err) {
        console.error("Error fetching mileage rates:", err);
        // Fallback to default 0.7 if fetch fails
      }
    };

    fetchMileageRates();
  }, []);

  updateInterpretationRef.current = updateInterpretation;

  useEffect(() => {
    if (shouldValidate) {
      if (
        !(
          
          interpretationStatusError ||
          timeStartError ||
          timeEndError ||
          actualStartError ||
          actualEndError ||
          linguistPayError
        )
      ) {
        updateInterpretationRef.current();
      }

      setShouldValidate(false);
    }
  }, [
    shouldValidate,
    interpretationStatusError,
    timeStartError,
    timeEndError,
    actualStartError,
    actualEndError,
    linguistPayError,
  ]);

  useEffect(() => {
    updateCurrentProgressRef.current();
  }, [
    interpretationStatus,
    timeStart,
    timeEnd,
    actualStart,
    actualEnd,
    linguistPay,
  ]);

  useEffect(() => {
    if (!isNewAssignment) {
      initialDataRef.current = {
        interpretationStatus: overlayData.InterpretationStatus,
        cancellationReason: overlayData.CancellationReason,
        linguistPay: overlayData.LinguistPay,
        additionalCost: overlayData.AdditionalCost,
        timeStart: overlayData.TimeStart,
        timeEnd: overlayData.TimeEnd,
      };
    }
  }, [isNewAssignment, overlayData]);

  useEffect(() => {
    if (!isNewAssignment && initialDataRef.current) {
      const dirty =
        interpretationStatus !== initialDataRef.current.interpretationStatus || //Error here
        (cancellationReason || "") !==
          (initialDataRef.current.cancellationReason || "") ||
        Number(linguistPay) !== Number(initialDataRef.current.linguistPay) ||
        (timeStart && timeStart.format("HH:mm:ss")) !==
          initialDataRef.current.timeStart ||
        (timeEnd && timeEnd.format("HH:mm:ss")) !==
          initialDataRef.current.timeEnd ||
        ((actualStart && actualStart.format("HH:mm:ss")) || "") !==
          (initialDataRef.current.actualStart || "") ||
        ((actualEnd && actualEnd.format("HH:mm:ss")) || "") !==
          (initialDataRef.current.actualEnd || "");
      setHasChanges(dirty);
    }
  }, [
    interpretationStatus,
    cancellationReason,
    linguistPay,
    timeStart,
    timeEnd,
    actualStart,
    actualEnd,
    isNewAssignment,
  ]);

  const saveInterpretation = () => {
    // If we reach here, either no warning needed or user confirmed
    setPendingSave(false);

    const errors = validateFields();
    const hasErrors = Object.values(errors).some(Boolean);
    if (hasErrors) {
      return;
    }

    updateInterpretationRef.current();
  };

  const attemptSave = () => {
    if (isSavingChanges) {
      return;
    }

    if (isLocked) {
      const lockedByName = lockedBy?.userName || "another user";
      showSnackbar(
        `This assignment is locked by ${lockedByName}`,
        theme,
        "warning",
      );
      return;
    }

    if (isNewAssignment && !triggerMissingFieldsShake()) {
      return;
    }

    saveInterpretation();
  };
  saveInterpretationRef.current = saveInterpretation;
  attemptSaveRef.current = attemptSave;

  

  useEffect(() => {
    const handleEnterSubmit = (event) => {
      const target = event.target;
      const tagName = target?.tagName;
      const isTextArea = tagName === "TEXTAREA";
      const isContentEditable = !!target?.isContentEditable;
      const isInsideDialog =
        target instanceof Element && !!target.closest('[role="dialog"]');

      if (
        event.key === "Enter" &&
        !event.shiftKey &&
        !isTextArea &&
        !isContentEditable &&
        !isInsideDialog
      ) {
        event.preventDefault();
        attemptSaveRef.current();
      }
    };

    window.addEventListener("keydown", handleEnterSubmit);
    return () => window.removeEventListener("keydown", handleEnterSubmit);
  }, []);

  // Calculate charges automatically when dependencies change
  useEffect(() => {
    // 2026-10-06 (a) model: manual mode (DisableAutoChargeCalc) no longer
    // skips this effect — the totals still compute, but ONLY the
    // non-overridden Total Linguist Pay is written (guard below).

    if (!totalDuration) return;

    // Helper to get decimal hours from a dayjs duration
    const toDecimal = (d) => (d && d.isValid ? d.hour() + d.minute() / 60 : 0);

    // Calculate durations
    const totalDecimal = toDecimal(totalDuration);
    const prepDecimal = toDecimal(preparationTime);
    const schedDecimal = toDecimal(schedulingTime);

    let durationDecimal = totalDecimal;
    const minHours = twoHoursMin ? 2 : 1;

    // Round to nearest quarter hour FIRST (normalize measured duration)
    durationDecimal = Math.ceil(durationDecimal * 4) / 4;

    // Then apply minimum hours only if short assignments are not allowed
    if (!allowShortAssignments) {
      if (durationDecimal <= 2 && minHours > 1) {
        durationDecimal = minHours;
      }
      if (minHours === 1 && durationDecimal < 1) {
        durationDecimal = minHours;
      }
    }

    let pay = 0;
    let totalLingPay = 0;
    let totalLingPayText = "";

    // Build cost value map for dynamic charge/totCost computation driven by active split-cost flags.
    const extraCostValues = {
      mileageCost: parseFloat(mileageCost || 0),
      parkingFeesTolls: parseFloat(parkingFeesTolls || 0),
      additionalCost: parseFloat(additionalCost || 0),
      additionalLinguistCost: parseFloat(additionalLinguistCost || 0),
      paymentFees: parseFloat(paymentFees || 0),
      equipmentCost: parseFloat(equipmentCost || 0),
      travelTimeCost: parseFloat(travelTimeCost || 0),
    };

    const baseOptionByKey = INTERPRETATION_BASE_COST_TYPE_OPTIONS.reduce(
      (acc, opt) => {
        acc[opt.key] = opt;
        return acc;
      },
      {},
    );

    const activeCostOptions =
      costTypeOptions.length > 0
        ? costTypeOptions
        : INTERPRETATION_BASE_COST_TYPE_OPTIONS.map((opt) => ({
            ...opt,
            effectiveIncludeInTotalCost: opt.defaultIncludeInTotalCost,
            effectiveIncludeInAccountCharge: opt.defaultIncludeInAccountCharge,
          }));

    const mappedActiveOptions = activeCostOptions
      .filter(
        (opt) =>
          opt.key !== "linguistPay" &&
          Object.prototype.hasOwnProperty.call(extraCostValues, opt.key),
      )
      .map((opt) => ({
        ...opt,
        includeInLinguistPay:
          typeof opt.includeInLinguistPay === "boolean"
            ? opt.includeInLinguistPay
            : !!baseOptionByKey[opt.key]?.includeInLinguistPay,
      }));

    if (mode === "Simultaneous") {
      // Calculate the "core" time (total - prep - sched), never negative
      const coreDecimal = Math.max(
        durationDecimal - prepDecimal - schedDecimal,
        0,
      );

      const rateVal = parseFloat(rate || 0);

      pay = rateVal * (prepDecimal + schedDecimal) + rateVal * coreDecimal;

      const totalLingExtras = mappedActiveOptions.filter(
        (opt) => opt.includeInLinguistPay,
      );
      const extraLingPaySum = totalLingExtras.reduce(
        (sum, opt) => sum + (extraCostValues[opt.key] || 0),
        0,
      );

      // TotalLinguistPay = base pay + all cost types marked includeInLinguistPay
      totalLingPay =
        rateVal * (prepDecimal + schedDecimal) +
        rateVal * coreDecimal +
        extraLingPaySum;

      const lingExtraText = totalLingExtras
        .map((opt) => (extraCostValues[opt.key] || 0).toFixed(2))
        .join(" + ");
      totalLingPayText = `${rateVal.toFixed(2)} x ${coreDecimal.toFixed(2)} + ${rateVal.toFixed(2)} x [${prepDecimal.toFixed(2)} + ${schedDecimal.toFixed(2)}]${lingExtraText ? ` + ${lingExtraText}` : ""}`;

      
    } else {
      // Consecutive: all time at 1x
      pay = parseFloat(rate || 0) * parseFloat(durationDecimal || 0);

      const totalLingExtras = mappedActiveOptions.filter(
        (opt) => opt.includeInLinguistPay,
      );
      const extraLingPaySum = totalLingExtras.reduce(
        (sum, opt) => sum + (extraCostValues[opt.key] || 0),
        0,
      );

      // TotalLinguistPay = base pay + all cost types marked includeInLinguistPay
      const rateVal = parseFloat(rate || 0);
      totalLingPay =
        rateVal * parseFloat(durationDecimal || 0) + extraLingPaySum;

      const lingExtraText = totalLingExtras
        .map((opt) => (extraCostValues[opt.key] || 0).toFixed(2))
        .join(" + ");
      totalLingPayText = `${rateVal.toFixed(2)} x ${parseFloat(durationDecimal || 0).toFixed(2)}${lingExtraText ? ` + ${lingExtraText}` : ""}`;

      
    }

    // 2026-10-06 (a) model — manual mode (DisableAutoChargeCalc): the totals
    // compute exactly like the auto path, but ONLY the non-overridden Total
    // Linguist Pay is written. The base Linguist Pay row and the helper text
    // stay untouched (manual behavior otherwise unchanged); AUTO stays as-is.
    if (disableAutomaticCalculationOfCharges) {
      if (!totalLinguistPayOverriddenRef.current) {
        const newTotalLingPayManual = totalLingPay.toFixed(2);
        if (newTotalLingPayManual !== totalLinguistPay) {
          setTotalLinguistPay(newTotalLingPayManual);
        }
      }
      return;
    }

    const newPay = pay.toFixed(2);
    if (newPay !== linguistPay) {
      setLinguistPay(newPay);
    }

    // Update TotalLinguistPay
    const newTotalLingPay = totalLingPay.toFixed(2);
    if (newTotalLingPay !== totalLinguistPay) {
      setTotalLinguistPay(newTotalLingPay);
    }

    // Set TotalLinguistPay helper text if it exists
    if (typeof totalLingPayText !== "undefined") {
      setTotalLinguistPayHelperText(totalLingPayText);
    }
    
  }, [
    interpretationStatus,
    totalDuration,
    rate,
    parkingFeesTolls,
    additionalCost,
    paymentFees,
    additionalLinguistCost,
    equipmentCost,
    mileageCost,
    travelTimeCost,
    mode,
    twoHoursMin,
    linguistPay,
    preparationTime,
    schedulingTime,
    disableAutomaticCalculationOfCharges,
    allowShortAssignments,
    totalLinguistPay,
    costTypeOptions,
  ]);

  // REMOVED 2026-09-22 (financial bug): the old "manual mode keeps Total
  // Cost / Total Linguist Pay aligned with Linguist Pay" effect forced both
  // totals to equal the base-pay row — dropping every extra (mileage,
  // parking, …) and, with the onChange cross-writes, corrupting the
  // Linguist Pay row itself on save. Manual mode leaves all values under
  // user control; nothing auto-overwrites.


  // Update mileage cost when mileage roundtrip or date changes
  useEffect(() => {
    if (!shouldAutoCalculateMileageCost) {
      return;
    }

    if (mileageRoundtrip && date && date.isValid()) {
      const mileageRate = getMileageRateForYear(date);
      setMileageCost(
        (parseFloat(mileageRoundtrip || 0) * mileageRate).toFixed(2),
      );
    } else {
      setMileageCost(0);
    }
  }, [
    mileageRoundtrip,
    date,
    getMileageRateForYear,
    shouldAutoCalculateMileageCost,
  ]);

  // Update travel time cost when travel time duration or client rate changes
  useEffect(() => {
    if (travelTimeDuration && travelTimeDuration.isValid()) {
      const decimalHours =
        (travelTimeDuration.hour() * 60 + travelTimeDuration.minute()) / 60;
      const calculatedCost = ((decimalHours * rate) / 2).toFixed(2);
      setTravelTimeCost(calculatedCost);
    } else {
      setTravelTimeCost(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [travelTimeDuration]);

  return (
    <>
      <Overlay
        id="linguist-assignment-finalize"
        open={true}
        onClose={handleBackButtonClick}
        title="Finalize Interpretation Assignment:"
        maxWidthPx={1200}
        footer={
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              width: "100%",
              flexDirection: "row",
              gap: isMobile ? 1 : 0,
            }}
          >
            {isMobile ? (
              <>
                <Tooltip title="Discard">
                  <IconButton
                    color="primary"
                    onClick={() => {
                      releaseLock();
                      setOverlayName("");
                    }}
                    sx={{
                      width: "40px",
                      height: "40px",
                      backgroundColor: theme.palette.background.default,
                      border: `2px solid ${theme.palette.primary.main}`,
                      borderRadius: "12px",
                      "&:hover": {
                        backgroundColor: theme.palette.primary.buttonHover,
                      },
                    }}
                  >
                    <DisabledByDefaultRoundedIcon />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Save">
                  <IconButton
                    onClick={attemptSave}
                    disabled={isSavingChanges || isLocked}
                    sx={{
                      width: "40px",
                      height: "40px",
                      backgroundColor: theme.palette.primary.main,
                      borderRadius: "12px",
                      color: "white",
                      "&:hover": {
                        backgroundColor:
                          theme.palette.primary.buttonHoverSelected,
                      },
                      "&:disabled": {
                        backgroundColor: theme.palette.grey[400],
                        color: theme.palette.grey[200],
                      },
                    }}
                  >
                    <SaveRoundedIcon />
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              <>
                <Button
                  color="primary"
                  variant="outlined"
                  size="large"
                  endIcon={<DisabledByDefaultRoundedIcon />}
                  onClick={() => {
                    releaseLock();
                    setOverlayName("");
                  }}
                  sx={{
                    borderRadius: "12px",
                  }}
                >
                  Discard
                </Button>

                <Box p="0 .5rem" />

                <Button
                  loading={isSavingChanges}
                  disabled={isSavingChanges || isLocked}
                  color="primary"
                  variant="contained"
                  size="large"
                  endIcon={<SaveRoundedIcon />}
                  type="button"
                  onClick={attemptSave}
                  sx={{
                    borderRadius: "12px",
                  }}
                >
                  Save
                </Button>
              </>
            )}
          </Box>
        }
      >
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              width: "100%",
              height: "50%",
              backgroundColor: selectedDateHoliday
                ? theme.palette.background.info
                : "transparent",
              //borderRadius: "0 0 1.5rem 1.5rem",
              zIndex: 0,
            }}
          />
          <Box
            component="form"
            sx={{
              position: "relative",
              zIndex: 1,
              display: "flex",
              p: "1rem 2.3rem 1rem 2.5rem",
              height: "auto", // Auto height to fit content
              minHeight: 0, // Allow shrinking
              width: "100%",
              borderRadius: "1.5rem",
              border: `1px solid ${theme.palette.background.default}`,
              backgroundColor: theme.palette.background.default,
              "& .MuiTextField-root": {
                m: 0,
                width: "100%",
                height: "auto",
              },
            }}
          >
            <Grid container spacing={2} width="100%">
              <Grid size={{ xs: 12, md: 12 }}>
                <TextField
                  disabled={!isEditable}
                  size="small"
                  select
                  id={"InterpretationStatus"}
                  label="Interpretation Status"
                  error={interpretationStatusError}
                  //defaultValue={interpretationStatus === null ? "Requested" : ""}
                  value={interpretationStatus}
                  onChange={(event) => {
                    setInterpretationStatus(event.target.value);
                    setInterpretationStatusError(event.target.value === null);

                    if (!event.target.value.startsWith("Cancel"))
                      setCancellationReason("");
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "& .MuiOutlinedInput-root": {
                      py: 0,
                      "&.MuiInputBase-root fieldset": {
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,
                          interpretationStatus || "",
                          overlayData.InterpretationStatus,
                        ),
                      },
                    },
                  }}
                >
                  {overlayData.InterpretationStatuses.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {interpretationStatus?.startsWith("Cancel") ? (
                <Grid container size={{ xs: 12, md: 12 }}>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      disabled={!isEditable}
                      size="small"
                      id={"CancellationReason"}
                      label="Cancellation Reason"
                      value={cancellationReason}
                      onChange={(event) => {
                        setCancellationReason(event.target.value);
                      }}
                      sx={{
                        "& .MuiInputBase-input": {
                          padding: "8.5px 12px !important",
                        },
                        "& .MuiOutlinedInput-root": {
                          py: 0,
                          "&.MuiInputBase-root fieldset": {
                            borderColor: getBorderColor(
                              theme,
                              isNewAssignment,
                              cancellationReason || "",
                              overlayData.CancellationReason,
                              true,
                            ),
                          },
                        },
                        height: "auto",
                      }}
                    />
                  </Grid>
                </Grid>
              ) : null}

              <Grid size={{ xs: 12, md: 4 }}>
                <CETimePicker
                  disabled={true}
                  value={timeStart || null}
                  label="Start Time"
                  slotProps={{
                    textField: {
                      size: "small",
                      error: timeStartError,
                    },
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "& .MuiOutlinedInput-root": {
                      py: 0,
                      "&.MuiInputBase-root fieldset": {
                        borderColor: getBorderColorTimes(
                          (timeStart && timeStart.format("HH:mm:ss")) || "",
                          overlayData.TimeStart || "",
                          (actualStart && actualStart.format("HH:mm:ss")) || "",
                        ),
                      },
                    },
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <CETimePicker
                  disabled={true}
                  value={timeEnd || null}
                  label="End Time"
                  slotProps={{
                    textField: {
                      size: "small",
                      error: timeEndError,
                    },
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "& .MuiOutlinedInput-root": {
                      py: 0,
                      "&.MuiInputBase-root fieldset": {
                        borderColor: getBorderColorTimes(
                          (timeEnd && timeEnd.format("HH:mm:ss")) || "",
                          overlayData.TimeEnd || "",
                          (actualEnd && actualEnd.format("HH:mm:ss")) || "",
                        ),
                      },
                    },
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <TimeField
                  disabled={true}
                  readOnly
                  label="Duration"
                  value={duration}
                  format="HH:mm"
                  size="small"
                  id={"Duration"}
                  sx={{
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                      cursor: "default",
                    },
                    "& .MuiOutlinedInput-root": {
                      py: 0,
                      "&.MuiInputBase-root fieldset": {
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,
                          (duration && duration.format("HH:mm:ss")) || "",
                          overlayData.Duration || "",
                          true,
                        ),
                      },
                    },
                  }}
                />
              </Grid>

              <Grid container>
                <Grid size={{ xs: 12 }}>
                  <Divider />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <CETimePicker
                    disabled={!isEditable}
                    value={actualStart || null}
                    label="Actual Start"
                    onChange={(newTime) => {
                      setActualStart(newTime);
                      setActualStartError(!newTime);

                      const tempActualDuration = getDurationValue(
                        newTime,
                        actualEnd,
                      );

                      setActualDuration(tempActualDuration);

                      const tempTotalDuration = sumDurationValues([
                        tempActualDuration && duration
                          ? Math.max(
                              tempActualDuration.hour() * 60 +
                                tempActualDuration.minute(),
                              duration.hour() * 60 + duration.minute(),
                            )
                          : tempActualDuration || duration || 0,
                        schedulingTime,
                        preparationTime,
                      ]);

                      setTotalDuration(tempTotalDuration);
                    }}
                    slotProps={{
                      textField: {
                        size: "small",
                        error: actualStartError,
                      },
                    }}
                    sx={{
                      "& .MuiInputBase-input": {
                        padding: "8.5px 12px !important",
                      },
                      "& .MuiOutlinedInput-root": {
                        py: 0,
                        "&.MuiInputBase-root fieldset": {
                          borderColor: getBorderColor(
                            theme,
                            isNewAssignment,
                            (actualStart && actualStart.format("HH:mm:ss")) ||
                              "",
                            overlayData.ActualStart || "",
                            true,
                          ),
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <CETimePicker
                    disabled={!isEditable}
                    value={actualEnd || null}
                    label="Actual End"
                    onChange={(newTime) => {
                      setActualEnd(newTime);
                      setActualEndError(!newTime);

                      const tempActualDuration = getDurationValue(
                        actualStart,
                        newTime,
                      );

                      setActualDuration(tempActualDuration);

                      const tempTotalDuration = sumDurationValues([
                        tempActualDuration && duration
                          ? Math.max(
                              tempActualDuration.hour() * 60 +
                                tempActualDuration.minute(),
                              duration.hour() * 60 + duration.minute(),
                            )
                          : tempActualDuration || duration || 0,
                        schedulingTime,
                        preparationTime,
                      ]);

                      setTotalDuration(tempTotalDuration);
                    }}
                    slotProps={{
                      textField: {
                        size: "small",
                        error: actualEndError,
                      },
                    }}
                    sx={{
                      "& .MuiInputBase-input": {
                        padding: "8.5px 12px !important",
                      },
                      "& .MuiOutlinedInput-root": {
                        py: 0,
                        "&.MuiInputBase-root fieldset": {
                          borderColor: getBorderColor(
                            theme,
                            isNewAssignment,
                            (actualEnd && actualEnd.format("HH:mm:ss")) || "",
                            overlayData.ActualEnd || "",
                            true,
                          ),
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TimeField
                    disabled={false}
                    readOnly
                    label="Actual Duration"
                    value={actualDuration}
                    format="HH:mm"
                    size="small"
                    id={"ActualDuration"}
                    slotProps={{
                      textField: {
                        InputProps: createTimeFieldAdornments(
                          theme,
                          !isEditable || !actualStart,
                          () => {
                            if (actualDuration && actualStart) {
                              // Decrement actual duration by 15 minutes
                              const currentMinutes =
                                actualDuration.hour() * 60 +
                                actualDuration.minute();
                              const newMinutes = Math.max(
                                0,
                                currentMinutes - 15,
                              );
                              const newActualDuration = dayjs()
                                .hour(Math.floor(newMinutes / 60))
                                .minute(newMinutes % 60)
                                .second(0);

                              // Update actual end time based on new duration
                              const newActualEnd = actualStart.add(
                                newMinutes,
                                "minute",
                              );

                              setActualDuration(newActualDuration);
                              setActualEnd(newActualEnd);
                              setActualEndError(!newActualEnd);

                              const tempTotalDuration = sumDurationValues([
                                newActualDuration && duration
                                  ? Math.max(
                                      newActualDuration.hour() * 60 +
                                        newActualDuration.minute(),
                                      duration.hour() * 60 + duration.minute(),
                                    )
                                  : newActualDuration || duration || 0,
                                schedulingTime,
                                preparationTime,
                              ]);

                              setTotalDuration(tempTotalDuration);
                            }
                          },
                          () => {
                            if (actualDuration && actualStart) {
                              // Increment actual duration by 15 minutes
                              const currentMinutes =
                                actualDuration.hour() * 60 +
                                actualDuration.minute();
                              const newMinutes = currentMinutes + 15;
                              const newActualDuration = dayjs()
                                .hour(Math.floor(newMinutes / 60))
                                .minute(newMinutes % 60)
                                .second(0);

                              // Update actual end time based on new duration
                              const newActualEnd = actualStart.add(
                                newMinutes,
                                "minute",
                              );

                              setActualDuration(newActualDuration);
                              setActualEnd(newActualEnd);
                              setActualEndError(!newActualEnd);

                              const tempTotalDuration = sumDurationValues([
                                newActualDuration && duration
                                  ? Math.max(
                                      newActualDuration.hour() * 60 +
                                        newActualDuration.minute(),
                                      duration.hour() * 60 + duration.minute(),
                                    )
                                  : newActualDuration || duration || 0,
                                schedulingTime,
                                preparationTime,
                              ]);

                              setTotalDuration(tempTotalDuration);
                            }
                          },
                        ),
                      },
                    }}
                    sx={{
                      "& .MuiInputBase-input": {
                        padding: "8.5px 12px !important",
                        cursor: "default",
                      },
                      "& .MuiOutlinedInput-root": {
                        py: 0,
                        "&.MuiInputBase-root fieldset": {
                          borderColor: getBorderColor(
                            theme,
                            isNewAssignment,
                            (actualDuration &&
                              actualDuration.format("HH:mm:ss")) ||
                              "",
                            overlayData.ActualDuration || "",
                            true,
                          ),
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TimeField
                    disabled={false}
                    readOnly
                    label="Scheduling Time"
                    value={schedulingTime}
                    format="HH:mm"
                    size="small"
                    id={"SchedulingTime"}
                    slotProps={{
                      textField: {
                        error: schedulingTimeError,
                        InputProps: createTimeFieldAdornments(
                          theme,
                          !isEditable,
                          () => {
                            if (schedulingTime) {
                              // Decrement scheduling time by 15 minutes
                              const currentMinutes =
                                schedulingTime.hour() * 60 +
                                schedulingTime.minute();
                              const newMinutes = Math.max(
                                0,
                                currentMinutes - 15,
                              );
                              const newSchedulingTime = dayjs()
                                .hour(Math.floor(newMinutes / 60))
                                .minute(newMinutes % 60)
                                .second(0);

                              setSchedulingTime(newSchedulingTime);
                              setSchedulingTimeError(!newSchedulingTime);

                              const tempTotalDuration = sumDurationValues([
                                actualDuration && duration
                                  ? Math.max(
                                      actualDuration.hour() * 60 +
                                        actualDuration.minute(),
                                      duration.hour() * 60 + duration.minute(),
                                    )
                                  : actualDuration || duration || 0,
                                newSchedulingTime,
                                preparationTime,
                              ]);

                              setTotalDuration(tempTotalDuration);

                              // Reset manual flags for charges when times change
                            }
                          },
                          () => {
                            if (schedulingTime) {
                              // Increment scheduling time by 15 minutes
                              const currentMinutes =
                                schedulingTime.hour() * 60 +
                                schedulingTime.minute();
                              const newMinutes = currentMinutes + 15;
                              const newSchedulingTime = dayjs()
                                .hour(Math.floor(newMinutes / 60))
                                .minute(newMinutes % 60)
                                .second(0);

                              setSchedulingTime(newSchedulingTime);
                              setSchedulingTimeError(!newSchedulingTime);

                              const tempTotalDuration = sumDurationValues([
                                actualDuration && duration
                                  ? Math.max(
                                      actualDuration.hour() * 60 +
                                        actualDuration.minute(),
                                      duration.hour() * 60 + duration.minute(),
                                    )
                                  : actualDuration || duration || 0,
                                newSchedulingTime,
                                preparationTime,
                              ]);

                              setTotalDuration(tempTotalDuration);
                            } else {
                              // Initialize with 15 minutes if null
                              const newSchedulingTime = dayjs()
                                .hour(0)
                                .minute(15)
                                .second(0);

                              setSchedulingTime(newSchedulingTime);
                              setSchedulingTimeError(!newSchedulingTime);

                              const tempTotalDuration = sumDurationValues([
                                actualDuration && duration
                                  ? Math.max(
                                      actualDuration.hour() * 60 +
                                        actualDuration.minute(),
                                      duration.hour() * 60 + duration.minute(),
                                    )
                                  : actualDuration || duration || 0,
                                newSchedulingTime,
                                preparationTime,
                              ]);

                              setTotalDuration(tempTotalDuration);
                            }
                          },
                        ),
                      },
                    }}
                    sx={{
                      "& .MuiInputBase-input": {
                        padding: "8.5px 12px !important",
                        cursor: "default",
                      },
                      "& .MuiOutlinedInput-root": {
                        py: 0,
                        "&.MuiInputBase-root fieldset": {
                          borderColor: getBorderColor(
                            theme,
                            isNewAssignment,
                            (schedulingTime &&
                              schedulingTime.format("HH:mm:ss")) ||
                              "",
                            overlayData.SchedulingTime || "",
                            true,
                          ),
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TimeField
                    disabled={false}
                    readOnly
                    label="Preparation Time"
                    value={preparationTime}
                    format="HH:mm"
                    size="small"
                    id={"PreparationTime"}
                    slotProps={{
                      textField: {
                        InputProps: createTimeFieldAdornments(
                          theme,
                          !isEditable,
                          () => {
                            if (preparationTime) {
                              // Decrement preparation time by 15 minutes
                              const currentMinutes =
                                preparationTime.hour() * 60 +
                                preparationTime.minute();
                              const newMinutes = Math.max(
                                0,
                                currentMinutes - 15,
                              );
                              const newPreparationTime = dayjs()
                                .hour(Math.floor(newMinutes / 60))
                                .minute(newMinutes % 60)
                                .second(0);

                              setPreparationTime(newPreparationTime);

                              const tempTotalDuration = sumDurationValues([
                                actualDuration && duration
                                  ? Math.max(
                                      actualDuration.hour() * 60 +
                                        actualDuration.minute(),
                                      duration.hour() * 60 + duration.minute(),
                                    )
                                  : actualDuration || duration || 0,
                                schedulingTime,
                                newPreparationTime,
                              ]);

                              setTotalDuration(tempTotalDuration);
                            }
                          },
                          () => {
                            if (preparationTime) {
                              // Increment preparation time by 15 minutes
                              const currentMinutes =
                                preparationTime.hour() * 60 +
                                preparationTime.minute();
                              const newMinutes = currentMinutes + 15;
                              const newPreparationTime = dayjs()
                                .hour(Math.floor(newMinutes / 60))
                                .minute(newMinutes % 60)
                                .second(0);

                              setPreparationTime(newPreparationTime);

                              const tempTotalDuration = sumDurationValues([
                                actualDuration && duration
                                  ? Math.max(
                                      actualDuration.hour() * 60 +
                                        actualDuration.minute(),
                                      duration.hour() * 60 + duration.minute(),
                                    )
                                  : actualDuration || duration || 0,
                                schedulingTime,
                                newPreparationTime,
                              ]);

                              setTotalDuration(tempTotalDuration);
                            } else {
                              // Initialize with 15 minutes if null
                              const newPreparationTime = dayjs()
                                .hour(0)
                                .minute(15)
                                .second(0);

                              setPreparationTime(newPreparationTime);

                              const tempTotalDuration = sumDurationValues([
                                actualDuration && duration
                                  ? Math.max(
                                      actualDuration.hour() * 60 +
                                        actualDuration.minute(),
                                      duration.hour() * 60 + duration.minute(),
                                    )
                                  : actualDuration || duration || 0,
                                schedulingTime,
                                newPreparationTime,
                              ]);

                              setTotalDuration(tempTotalDuration);
                            }
                          },
                        ),
                      },
                    }}
                    sx={{
                      "& .MuiInputBase-input": {
                        padding: "8.5px 12px !important",
                        cursor: "default",
                      },
                      "& .MuiOutlinedInput-root": {
                        py: 0,
                        "&.MuiInputBase-root fieldset": {
                          borderColor: getBorderColor(
                            theme,
                            isNewAssignment,
                            (preparationTime &&
                              preparationTime.format("HH:mm:ss")) ||
                              "",
                            overlayData.PreparationTime || "",
                            true,
                          ),
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TimeField
                    disabled={false}
                    readOnly
                    label="Total Duration"
                    value={totalDuration}
                    format="HH:mm"
                    size="small"
                    id={"TotalDuration"}
                    slotProps={{
                      textField: {
                        InputProps: createTimeFieldAdornments(
                          theme,
                          false,
                          () => {
                            if (totalDuration) {
                              // Decrement total duration by 15 minutes
                              const currentMinutes =
                                totalDuration.hour() * 60 +
                                totalDuration.minute();
                              const newMinutes = Math.max(
                                0,
                                currentMinutes - 15,
                              );
                              const newTotalDuration = dayjs()
                                .hour(Math.floor(newMinutes / 60))
                                .minute(newMinutes % 60)
                                .second(0);

                              setTotalDuration(newTotalDuration);

                              // Reset manual flags for charges when times change
                            }
                          },
                          () => {
                            if (totalDuration) {
                              // Increment total duration by 15 minutes
                              const currentMinutes =
                                totalDuration.hour() * 60 +
                                totalDuration.minute();
                              const newMinutes = currentMinutes + 15;
                              const newTotalDuration = dayjs()
                                .hour(Math.floor(newMinutes / 60))
                                .minute(newMinutes % 60)
                                .second(0);

                              setTotalDuration(newTotalDuration);

                              // Reset manual flags for charges when times change
                            } else {
                              // Initialize with 15 minutes if null
                              const newTotalDuration = dayjs()
                                .hour(0)
                                .minute(15)
                                .second(0);

                              setTotalDuration(newTotalDuration);

                              // Reset manual flags for charges when times change
                            }
                          },
                        ),
                      },
                    }}
                    sx={{
                      "& .MuiInputBase-input": {
                        padding: "8.5px 12px !important",
                        cursor: "default",
                      },
                      "& .MuiOutlinedInput-root": {
                        py: 0,
                        "&.MuiInputBase-root fieldset": {
                          borderColor: getBorderColor(
                            theme,
                            isNewAssignment,
                            (totalDuration &&
                              totalDuration.format("HH:mm:ss")) ||
                              "",
                            overlayData.TotalDuration || "",
                            true,
                          ),
                        },
                      },
                    }}
                  />
                </Grid>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Box
                  sx={{
                    mb: 0,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <Typography variant="subtitle2">Costs</Typography>
                  <IconButton
                    size="small"
                    onClick={addCostRow}
                    disabled={
                      !isEditable ||
                      costRows.length >=
                        costTypeOptions.filter(
                          (option) =>
                            option.key === "linguistPay" || option.isEnabled,
                        ).length
                    }
                    sx={{ p: 0.5 }}
                  >
                    <AddRoundedIcon fontSize="small" />
                  </IconButton>
                  <Divider sx={{ flex: 1, ml: 0.5 }} />
                </Box>
              </Grid>

              {costRows.length > 0 && (
                <Grid container spacing={2} size={{ xs: 12 }}>
                  {costRows.map((row) => {
                    const selectedCostTypeKeys = costRows
                      .filter((item) => item.id !== row.id)
                      .map((item) => item.typeKey);
                    const availableOptionsForRow = costTypeOptions.filter(
                      (option) =>
                        option.key === row.typeKey ||
                        (option.isEnabled &&
                          !selectedCostTypeKeys.includes(option.key)),
                    );

                    const currentValue = getCostValueByType(row.typeKey);
                    const isMileageRow = row.typeKey === "mileageCost";
                    const isTravelTimeRow = row.typeKey === "travelTimeCost";
                    const isLinguistPayRow = row.typeKey === "linguistPay";
                    const isLockedLinguistPayRow =
                      row.typeKey === "linguistPay";
                    const costTypeBorderColor = row.typeKey
                      ? theme.palette.primary.main
                      : theme.palette.error.main;

                    return (
                      <Grid size={{ xs: 12 }} key={`cost-row-${row.id}`}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1.5,
                          }}
                        >
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Grid container spacing={1.5}>
                              <Grid
                                size={{
                                  xs: 12,
                                  md: isMileageRow || isTravelTimeRow ? 4 : 6,
                                }}
                              >
                                <TextField
                                  disabled={
                                    !isEditable || isLockedLinguistPayRow
                                  }
                                  select
                                  size="small"
                                  label="Cost Type"
                                  value={row.typeKey}
                                  onChange={(event) =>
                                    updateCostRowType(
                                      row.id,
                                      event.target.value,
                                    )
                                  }
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      "& .MuiInputBase-input": {
                                        padding: "8.5px 12px !important",
                                      },
                                      "&.MuiInputBase-root fieldset": {
                                        py: 0,
                                        borderColor: costTypeBorderColor,
                                      },
                                    },
                                  }}
                                >
                                  {availableOptionsForRow.map((option) => (
                                    <MenuItem
                                      key={option.key}
                                      value={option.key}
                                    >
                                      {option.label}
                                    </MenuItem>
                                  ))}
                                </TextField>
                              </Grid>

                              {isMileageRow ? (
                                <Grid size={{ xs: 12, md: 4 }}>
                                  <TextField
                                    disabled={!isEditable}
                                    size="small"
                                    id={"MileageRoundtrip"}
                                    label="Mileage Roundtrip"
                                    error={mileageRoundtripError}
                                    value={mileageRoundtrip}
                                    onFocus={() => {
                                      if (Number(mileageRoundtrip) === 0) {
                                        setMileageRoundtrip("");
                                      }
                                    }}
                                    onBlur={() => {
                                      if (Number(mileageRoundtrip) === 0) {
                                        setMileageRoundtrip(0);
                                      }
                                    }}
                                    onChange={(event) => {
                                      setShouldAutoCalculateMileageCost(true);
                                      setMileageRoundtrip(event.target.value);
                                      setMileageRoundtripError(
                                        event.target.value === null,
                                      );
                                      const mileageRate = getMileageRateForYear(
                                        date || dayjs.tz().toDate(),
                                      );
                                      setMileageCost(
                                        event.target.value
                                          ? (
                                              event.target.value * mileageRate
                                            ).toFixed(2)
                                          : "",
                                      );
                                    }}
                                    sx={{
                                      "& .MuiInputBase-input": {
                                        padding: "8.5px 12px !important",
                                      },
                                      "& .MuiOutlinedInput-root": {
                                        py: 0,
                                        "&.MuiInputBase-root fieldset": {
                                          borderColor: getBorderColor(
                                            theme,
                                            isNewAssignment,
                                            Number(mileageRoundtrip) || "",
                                            Number(
                                              overlayData.MileageRoundtrip,
                                            ) || "",
                                            true,
                                          ),
                                        },
                                      },
                                    }}
                                  />
                                </Grid>
                              ) : null}

                              {isTravelTimeRow ? (
                                <Grid size={{ xs: 12, md: 4 }}>
                                  <TimeField
                                    disabled={!isEditable}
                                    label="Travel Time Duration"
                                    value={travelTimeDuration}
                                    format="HH:mm"
                                    size="small"
                                    id="TravelTimeDuration"
                                    onChange={(newValue) => {
                                      setTravelTimeDuration(newValue);
                                      if (newValue && newValue.isValid()) {
                                        const decimalHours =
                                          (newValue.hour() * 60 +
                                            newValue.minute()) /
                                          60;

                                        setTravelTimeCost(
                                          ((decimalHours * rate) / 2).toFixed(
                                            2,
                                          ),
                                        );
                                      } else {
                                        setTravelTimeCost(0);
                                      }
                                    }}
                                    slotProps={{
                                      textField: {
                                        InputProps: createTimeFieldAdornments(
                                          theme,
                                          !isEditable,
                                          () => {
                                            const current =
                                              travelTimeDuration ||
                                              dayjs()
                                                .hour(0)
                                                .minute(0)
                                                .second(0);
                                            const currentMinutes =
                                              current.hour() * 60 +
                                              current.minute();
                                            const newMinutes = Math.max(
                                              0,
                                              currentMinutes - 15,
                                            );
                                            setTravelTimeDuration(
                                              dayjs()
                                                .hour(
                                                  Math.floor(newMinutes / 60),
                                                )
                                                .minute(newMinutes % 60)
                                                .second(0),
                                            );
                                          },
                                          () => {
                                            const current =
                                              travelTimeDuration ||
                                              dayjs()
                                                .hour(0)
                                                .minute(0)
                                                .second(0);
                                            const currentMinutes =
                                              current.hour() * 60 +
                                              current.minute();
                                            const newMinutes =
                                              currentMinutes + 15;
                                            setTravelTimeDuration(
                                              dayjs()
                                                .hour(
                                                  Math.floor(newMinutes / 60),
                                                )
                                                .minute(newMinutes % 60)
                                                .second(0),
                                            );
                                          },
                                        ),
                                        sx: {
                                          "& .MuiInputBase-input": {
                                            padding: "8.5px 12px !important",
                                          },
                                          "& .MuiOutlinedInput-root": {
                                            py: 0,
                                            "&.MuiInputBase-root fieldset": {
                                              borderColor: getBorderColor(
                                                theme,
                                                isNewAssignment,
                                                travelTimeDuration
                                                  ? travelTimeDuration.format(
                                                      "HH:mm:ss",
                                                    )
                                                  : "",
                                                overlayData.TravelTimeDuration ||
                                                  "",
                                                true,
                                              ),
                                            },
                                          },
                                        },
                                      },
                                    }}
                                  />
                                </Grid>
                              ) : null}

                              <Grid
                                size={{
                                  xs: 12,
                                  md: isMileageRow || isTravelTimeRow ? 4 : 6,
                                }}
                              >
                                <TextField
                                  disabled={!isEditable}
                                  size="small"
                                  label={
                                    row.typeKey === "mileageCost"
                                      ? "Mileage Cost"
                                      : row.typeKey === "travelTimeCost"
                                        ? "Travel Time Cost"
                                        : costTypeOptions.find(
                                            (option) =>
                                              option.key === row.typeKey,
                                          )?.label || "Amount"
                                  }
                                  slotProps={{
                                    input: {
                                      readOnly:
                                        (isLinguistPayRow &&
                                          !disableAutomaticCalculationOfCharges) ||
                                        isTravelTimeRow,
                                      startAdornment: (
                                        <InputAdornment position="start">
                                          $
                                        </InputAdornment>
                                      ),
                                    },
                                  }}
                                  value={currentValue}
                                  onFocus={() => {
                                    if (Number(currentValue) === 0) {
                                      setCostValueByType(row.typeKey, "");
                                    }
                                  }}
                                  onBlur={() => {
                                    if (
                                      Number(
                                        getCostValueByType(row.typeKey),
                                      ) === 0
                                    ) {
                                      setCostValueByType(row.typeKey, 0);
                                    }
                                  }}
                                  onChange={(event) => {
                                    setCostValueByType(
                                      row.typeKey,
                                      event.target.value,
                                    );
                                  }}
                                  sx={{
                                    "& .MuiInputBase-input": {
                                      padding: "8.5px 12px !important",
                                    },
                                    "& .MuiOutlinedInput-root": {
                                      py: 0,
                                      "&.MuiInputBase-root fieldset": {
                                        borderColor: getBorderColor(
                                          theme,
                                          isNewAssignment,
                                          Number(currentValue) || "",
                                          Number(
                                            getOriginalCostValueByType(
                                              row.typeKey,
                                            ),
                                          ) || "",
                                          true,
                                        ),
                                      },
                                    },
                                  }}
                                />
                              </Grid>
                            </Grid>
                          </Box>
                          <Box
                            sx={{ flex: "0 0 auto", mt: { xs: 0, md: 0.5 } }}
                          >
                            <IconButton
                              disabled={!isEditable || isLockedLinguistPayRow}
                              onClick={() => removeCostRow(row.id)}
                              size="small"
                            >
                              <ClearRoundedIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              )}

              <Grid size={{ xs: 12 }}>
                <Divider sx={{ my: 0.5 }} />
              </Grid>

              {costRows.some(
                (row) =>
                  row.typeKey === "linguistPay" ||
                  row.typeKey === "additionalLinguistCost",
              ) ? (
                <Grid container size={{ xs: 12 }}>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      disabled={!isEditable}
                      size="small"
                      id={"TotalLinguistPay"}
                      label="Total Linguist Pay"
                      helperText={totalLinguistPayHelperText}
                      slotProps={{
                        input: {
                          readOnly: !disableAutomaticCalculationOfCharges,
                          startAdornment: (
                            <InputAdornment position="start">$</InputAdornment>
                          ),
                        },
                      }}
                      value={totalLinguistPay}
                      onChange={(event) => {
                        // 2026-09-22 (financial bug): must NEVER write into
                        // the Linguist Pay cost ROW — the old cross-write
                        // copied the TOTAL into the base-pay row on save.
                        // 2026-10-06 (a) model: a non-empty entry is a user
                        // override; clearing drops it and the effect above
                        // re-fills the live computed pay (totalLinguistPay is
                        // one of its deps, so the refill runs immediately).
                        const nextValue = event.target.value;
                        totalLinguistPayOverriddenRef.current =
                          nextValue !== "";
                        setTotalLinguistPay(nextValue);
                      }}
                      sx={{
                        "& .MuiInputBase-input": {
                          padding: "8.5px 12px !important",
                        },
                        "& .MuiOutlinedInput-root": {
                          py: 0,
                          "&.MuiInputBase-root fieldset": {
                            borderColor:
                              totalLinguistPay === null ||
                              totalLinguistPay === ""
                                ? theme.palette.grey.main
                                : theme.palette.primary.main,
                          },
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              ) : null}

              <Grid container spacing={1.5} sx={{ pb: 1 }}>
                <Grid size={{ xs: 12 }}>
                  <Divider />
                </Grid>
              </Grid>
            </Grid>
          </Box>
    </Overlay>

      {/* Edit Lock Dialog */}
      <EditLockDialog
        open={isLocked}
        lockedBy={lockedBy}
        onClose={() => {
          releaseLock();
          setOverlayName("");
        }}
      />
    </>
  );
};

export default LinguistAssignmentFinalize;
